import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import './HeroCarousel.css';

// Import dummy images (fallback)
import dammy1 from '../../assets/images/dammy_1.jpeg';
import dammy2 from '../../assets/images/dammy_2.jpeg';
import dammy3 from '../../assets/images/dammy_3.jpeg';
import dammy4 from '../../assets/images/dammy_4.jpeg';
import dammy5 from '../../assets/images/dammy_5.jpeg';

interface Slider {
    id: number;
    photo_url: string;
    is_active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

interface ImageSlide {
    id: string;
    image: string;
    alt: string;
}

const HeroCarousel: React.FC = () => {
    const { isRTL } = useLanguage();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [slides, setSlides] = useState<ImageSlide[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fallback slides - wrapped in useMemo
    const fallbackSlides: ImageSlide[] = useMemo(() => [
        {
            id: 'slide-1',
            image: dammy1,
            alt: 'Product Image 1'
        },
        {
            id: 'slide-2',
            image: dammy2,
            alt: 'Product Image 2'
        },
        {
            id: 'slide-3',
            image: dammy3,
            alt: 'Product Image 3'
        },
        {
            id: 'slide-4',
            image: dammy4,
            alt: 'Product Image 4'
        },
        {
            id: 'slide-5',
            image: dammy5,
            alt: 'Product Image 5'
        }
    ], []);

    // Fetch active sliders from API
    useEffect(() => {
        const fetchSliders = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('https://api.dawaak-andna.com/api/sliders/active');
                
                if (!response.ok) {
                    throw new Error('Failed to fetch sliders');
                }

                const data = await response.json();
                
                // Transform API data to ImageSlide format
                if (data.sliders && data.sliders.length > 0) {
                    const transformedSlides: ImageSlide[] = data.sliders.map((slider: Slider) => ({
                        id: `slider-${slider.id}`,
                        image: `https://api.dawaak-andna.com${slider.photo_url}`,
                        alt: `Slider ${slider.id}`
                    }));
                    setSlides(transformedSlides);
                } else {
                    // Use fallback slides if no sliders from API
                    setSlides(fallbackSlides);
                }
                setError(null);
            } catch (err) {
                console.error('Error fetching sliders:', err);
                setError('Failed to load sliders');
                // Use fallback slides on error
                setSlides(fallbackSlides);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSliders();
    }, [fallbackSlides]);

    // Auto-play carousel
    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
        }, 1500); // Reduced from 6000ms to 3000ms (3 seconds)

        return () => clearInterval(interval);
    }, [isAutoPlaying, slides.length]);

    const goToSlide = (index: number) => {
        setCurrentSlideIndex(index);
        setIsAutoPlaying(false);
        // Resume auto-play after 10 seconds
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    const goToPrevious = () => {
        setCurrentSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    const goToNext = () => {
        setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    // Show loading state with shimmer
    if (isLoading) {
        return (
            <section className="hero-carousel">
                <div className="container">
                    <div className="carousel-container">
                        <div className="carousel-slide active shimmer-loading" style={{ 
                            height: '400px'
                        }}>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Show error state (with fallback slides already loaded)
    if (error && slides.length === 0) {
        return (
            <section className="hero-carousel">
                <div className="container">
                    <div className="carousel-container">
                        <div className="carousel-slide active" style={{ 
                            height: '400px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            background: '#f5f5f5'
                        }}>
                            <p>No sliders available</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="hero-carousel">
            <div className="container">
                <div className="carousel-container wheel-carousel">
                    {/* All slides stacked with wheel effect */}
                    <div className="carousel-slides-wrapper">
                        {slides.map((slide, index) => {
                            const position = (index - currentSlideIndex + slides.length) % slides.length;
                            const isActive = index === currentSlideIndex;
                            const isPrev = position === slides.length - 1;
                            const isNext = position === 1;
                            
                            return (
                                <div
                                    key={slide.id}
                                    className={`carousel-slide-card ${
                                        isActive ? 'active' : 
                                        isPrev ? 'prev' : 
                                        isNext ? 'next' : 
                                        'hidden'
                                    }`}
                                    onClick={() => !isActive && goToSlide(index)}
                                    style={{
                                        backgroundImage: `url(${slide.image})`,
                                        backgroundSize: 'contain',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    }}
                                >
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation Arrows */}
                    <button
                        className={`carousel-nav carousel-nav-prev ${isRTL ? 'rtl' : ''}`}
                        onClick={goToPrevious}
                        aria-label="Previous slide"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <button
                        className={`carousel-nav carousel-nav-next ${isRTL ? 'rtl' : ''}`}
                        onClick={goToNext}
                        aria-label="Next slide"
                    >
                        <ArrowRight size={20} />
                    </button>

                    {/* Slide Indicators */}
                    <div className="carousel-indicators">
                        {slides.map((slide, index) => (
                            <button
                                key={slide.id}
                                className={`carousel-indicator ${index === currentSlideIndex ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroCarousel;